import React, { useState, useMemo, useEffect } from 'react';
import { devLog, devWarn, devError } from '../utils/devLogger';
import styled from 'styled-components';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDevice } from '../context/DeviceContext';
import { useAdminAuth } from '../context/AdminAuthContext';

// LogOut 아이콘만 lucide-react에서 사용
import { LogOut as LogoutIcon } from 'lucide-react';

import ResponsiveSidebar from '../components/customSidebar/ResponsiveSidebar';
import { MenuItemConfig } from '../components/customSidebar/CustomSidebar';
import PageWrapper from '../components/PageWrapper';
import LogoutConfirmModal from '../components/LogoutConfirmModal';
import CMSIcon from '../components/CMSIcon';

interface CMSLayoutProps {
  children: React.ReactNode;
}

const CMSLayout: React.FC<CMSLayoutProps> = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const device = useDevice();
  const { isLoggedIn, ready, logout } = useAdminAuth();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);

  // 관리자 인증 확인
  useEffect(() => {
    if (ready && !isLoggedIn) {
      devLog('CMSLayout: 인증되지 않은 사용자, 로그인 페이지로 리다이렉트');
      navigate('/cms/login');
    }
  }, [ready, isLoggedIn, navigate]);

  const toggleSidebar = () => setIsCollapsed((prev) => !prev);

  const handleLogout = () => {
    setShowLogoutModal(true);
  };

  const handleConfirmLogout = () => {
    // AdminAuthContext의 logout 함수 사용
    logout();
    setShowLogoutModal(false);
    navigate('/cms/login');
  };

  const handleCloseLogoutModal = () => {
    setShowLogoutModal(false);
  };

  const effectiveSidebarExpanded = useMemo(
    () => (device === 'mobile' ? isMobileSidebarOpen : !isCollapsed),
    [device, isMobileSidebarOpen, isCollapsed]
  );

  const menuItems: MenuItemConfig[] = useMemo(() => [
    { 
      icon: <CMSIcon name="admin" isActive={location.pathname === '/cms/admin'} />, 
      title: '관리자회원관리', 
      path: '/cms/admin' 
    },
    { 
      icon: <CMSIcon name="users" isActive={location.pathname === '/cms/users'} />, 
      title: '고객회원관리', 
      path: '/cms/users' 
    },
    { 
      icon: <CMSIcon name="auctioneers" isActive={location.pathname.startsWith('/cms/auction')} />, 
      title: '경매관리', 
      path: '/cms/auction/auctioneers', // 기본적으로 경매사로 이동
      subMenu: [
        { icon: <span style={{ width: '24px', height: '24px' }} />, title: '경매사', path: '/cms/auction/auctioneers' },
        { icon: <span style={{ width: '24px', height: '24px' }} />, title: '경매', path: '/cms/auction/auctions' },
        { icon: <span style={{ width: '24px', height: '24px' }} />, title: 'LOT', path: '/cms/auction/lots' },
      ]
    },
    { 
      icon: <CMSIcon name="bidding" isActive={location.pathname === '/cms/bidding'} />, 
      title: '실시간 입찰 모니터링', 
      path: '/cms/bidding' 
    },
    { 
      icon: <CMSIcon name="payment" isActive={location.pathname === '/cms/payment'} />, 
      title: '결제관리', 
      path: '/cms/payment' 
    },
    { 
      icon: <CMSIcon name="notification" isActive={location.pathname === '/cms/notification'} />, 
      title: '알림관리', 
      path: '/cms/notification' 
    },
    { 
      icon: <CMSIcon name="terms" isActive={location.pathname === '/cms/terms'} />, 
      title: '약관관리', 
      path: '/cms/terms' 
    },
  ], [location.pathname]);

  const matchedMenu = useMemo(() => {
    // 하위 메뉴에서 먼저 매치되는 메뉴 찾기 (우선순위)
    for (const item of menuItems) {
      if (item.subMenu) {
        const subMatch = item.subMenu.find((subItem) => subItem.path === location.pathname);
        if (subMatch) return subMatch; // 하위 메뉴의 타이틀 반환
      }
    }
    
    // 하위 메뉴에서 매치되지 않으면 직접 매치되는 메뉴 찾기
    const directMatch = menuItems.find((item) => item.path === location.pathname);
    if (directMatch) return directMatch;
    
    return undefined;
  }, [location.pathname, menuItems]);

  const pageTitle = matchedMenu?.title ?? '';

  // 인증 체크 - 로그인 상태와 관리자 ID 확인
  useEffect(() => {
    const currentPath = location.pathname;
    
    // 로그인 페이지에 있으면 체크하지 않음
    if (currentPath === '/cms/login') {
      return;
    }
    
    if (ready) {
      const adminId = sessionStorage.getItem('adminId');
      
      if (!isLoggedIn || !adminId) {
        navigate('/cms/login');
      }
    }
  }, [ready, isLoggedIn, navigate, location.pathname]);

  // 인증이 완료되지 않았으면 로딩 표시
  if (!ready) {
    return <div>Loading...</div>;
  }

  // 로그인되지 않았으면 렌더링하지 않음 (useEffect에서 리다이렉트 처리)
  if (!isLoggedIn) {
    return null;
  }

  return (
    <OuterLayoutContainer className="cms-layout">
      <ResponsiveSidebar
        isCollapsed={isCollapsed}
        toggleSidebar={toggleSidebar}
        menuItems={menuItems}
        footerIcon={<LogoutIcon />}
        onFooterClick={handleLogout}
        onMobileSidebarOpenChange={setIsMobileSidebarOpen}
      >
        <SidebarHeader $isCollapsed={isCollapsed}>
          {/* <LogoContainer>
            <img src="/cms/logo.png" alt="CMS Logo" style={{ width: '130px' }} />
          </LogoContainer> */}
        </SidebarHeader>
      </ResponsiveSidebar>

      <MainContent $device={device} $isSidebarExpanded={effectiveSidebarExpanded}>
        <Container>
          <TopHeader>
            <h1>{pageTitle}</h1>
          </TopHeader>
          <PageWrapper>
            {children}
          </PageWrapper>
        </Container>
      </MainContent>
      
      <LogoutConfirmModal
        isOpen={showLogoutModal}
        onClose={handleCloseLogoutModal}
        onConfirm={handleConfirmLogout}
        language="ko"
      />
    </OuterLayoutContainer>
  );
};

export default CMSLayout;

// --- Styled Components ---

const OuterLayoutContainer = styled.div`
  /* min-width: 1450px; */
  height: 100vh;
  background-color: #e6e7e9;
  display: flex;
  overflow: hidden;
`;

const MainContent = styled.div<{
  $device: 'mobile' | 'tablet' | 'desktop';
  $isSidebarExpanded: boolean;
}>`
  transition: all 0.3s ease;
  flex: 1;
  box-sizing: border-box;
  height: 100vh;
  overflow-y: auto;
  overflow-x: auto;
  background-color: #e6e7e9;

  ${({ $device, $isSidebarExpanded }) => {
    if ($device === 'mobile') {
      return `
        margin-top: 0px;
        margin-left: ${$isSidebarExpanded ? '250px' : '0'};
      `;
    } else {
      return `
        padding-top: 0px;
        margin-left: ${$isSidebarExpanded ? '250px' : '80px'};
      `;
    }
  }}
`;

const Container = styled.div`
  /* padding: 0px 20px 20px 20px; */
  margin: 0 auto;
  max-width: none;
  background-color: #e6e7e9;
`;

const TopHeader = styled.div`
  text-align: left;
  padding: 20px 20px 0px 20px;
  
  h1 {
    font-size: 24px;
    font-weight: 600;
    color: #333;
    margin: 0;
  }
`;

const SidebarHeader = styled.div<{ $isCollapsed: boolean }>`
  padding: 16px;
  text-align: center;
  border-bottom: 1px solid #3a3f4e;
  background-color: #2c2e3c;
`;
